// Login form component for multi-tenant platform
// Requirements: 2.1, 2.2, 2.4

import React, { useState, useEffect } from 'react';
import { AlertTriangleIcon } from '../common/icons';
import { LoginCredentials, FormValidation } from '../../types/auth';
import { useAuth } from '../../contexts/AuthContext';
import { CognitoAuthService } from '../../services/cognitoAuthService';
import './LoginForm.css';

interface LoginFormProps {
  onLoginSuccess: (user: any) => void;
  onSwitchToRegister: () => void;
  loading?: boolean;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onLoginSuccess,
  onSwitchToRegister,
  loading = false
}) => {
  const { login, forceLogout } = useAuth();
  const [credentials, setCredentials] = useState<LoginCredentials>({
    email: '',
    password: ''
  });

  const [validation, setValidation] = useState<FormValidation>({
    email: { isValid: true, error: '' },
    password: { isValid: true, error: '' }
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showClearSession, setShowClearSession] = useState(false);

  // Real-time email validation
  useEffect(() => {
    if (credentials.email) {
      const emailValidation = CognitoAuthService.validateEmail(credentials.email);
      setValidation(prev => ({
        ...prev,
        email: emailValidation
      }));
    } else {
      setValidation(prev => ({
        ...prev,
        email: { isValid: true, error: '' }
      }));
    }
  }, [credentials.email]);

  const handleInputChange = (field: keyof LoginCredentials, value: string) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific errors when user starts typing
    if (validation[field] && !validation[field].isValid) {
      setValidation(prev => ({
        ...prev,
        [field]: { isValid: true, error: '' }
      }));
    }

    // Clear general error
    if (error) {
      setError('');
    }
  };

  const validateForm = (): boolean => {
    const emailValidation = CognitoAuthService.validateEmail(credentials.email);
    const passwordValid = credentials.password.length > 0;

    const newValidation: FormValidation = {
      email: emailValidation,
      password: {
        isValid: passwordValid,
        error: passwordValid ? '' : 'Password is required'
      }
    };

    setValidation(newValidation);

    return emailValidation.isValid && passwordValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting || loading) return;

    // Validate form
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // Use the auth context login method which properly updates the state
      await login(credentials.email, credentials.password);
      
      // Get the current user from CognitoAuthService after successful login
      const user = CognitoAuthService.getCurrentUser();
      if (user) {
        onLoginSuccess(user);
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      // Handle specific error messages
      if (err.message?.includes('There is already a signed in user') || 
          err.message?.includes('Authentication conflict')) {
        setError('Authentication conflict detected. Try clearing your session below or refresh the page.');
        setShowClearSession(true);
      } else if (err.message?.includes('Invalid credentials') || 
                 err.message?.includes('Invalid email or password')) {
        setError('Invalid email or password. Please try again.');
      } else if (err.message?.includes('Account is not active')) {
        setError('Your account is not active. Please contact support.');
      } else if (err.message?.includes('Network Error')) {
        setError('Unable to connect to the server. Please check your internet connection.');
      } else if (err.message?.includes('verify your email')) {
        setError('Please verify your email address before signing in.');
      } else {
        setError(err.message || 'Login failed. Please try again.');
        
        // Show clear session option for any authentication error
        if (err.message?.includes('Authentication') || err.message?.includes('signed in')) {
          setShowClearSession(true);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearSession = async () => {
    try {
      setIsSubmitting(true);
      setError('');
      
      await forceLogout();
      
      setError('Session cleared successfully. You can now try logging in again.');
      setShowClearSession(false);
      
      // Clear the form
      setCredentials({ email: '', password: '' });
    } catch (error: any) {
      console.error('Clear session error:', error);
      setError('Failed to clear session. Please refresh the page and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = validation.email.isValid && 
                     validation.password.isValid && 
                     credentials.email && 
                     credentials.password;

  return (
    <div className="login-form">
      <div className="login-form__header">
        <h2>Sign In</h2>
        <p>Welcome back! Please sign in to your account.</p>
      </div>

      <form onSubmit={handleSubmit} className="login-form__form">
        {error && (
          <div className="login-form__error" role="alert">
            <AlertTriangleIcon width={16} height={16} className="login-form__error-icon" />
            {error}
          </div>
        )}

        <div className="login-form__field">
          <label htmlFor="email" className="login-form__label">
            Email Address
          </label>
          <input
            id="email"
            type="email"
            value={credentials.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`login-form__input ${
              !validation.email.isValid ? 'login-form__input--error' : ''
            }`}
            placeholder="Enter your email address"
            disabled={isSubmitting || loading}
            autoComplete="email"
            required
          />
          {!validation.email.isValid && (
            <span className="login-form__field-error">
              {validation.email.error}
            </span>
          )}
        </div>

        <div className="login-form__field">
          <label htmlFor="password" className="login-form__label">
            Password
          </label>
          <div className="login-form__password-wrapper">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={credentials.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              className={`login-form__input ${
                !validation.password.isValid ? 'login-form__input--error' : ''
              }`}
              placeholder="Enter your password"
              disabled={isSubmitting || loading}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="login-form__password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              disabled={isSubmitting || loading}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? '👁️' : '👁️‍🗨️'}
            </button>
          </div>
          {!validation.password.isValid && (
            <span className="login-form__field-error">
              {validation.password.error}
            </span>
          )}
        </div>

        <button
          type="submit"
          className={`login-form__submit ${
            !isFormValid || isSubmitting || loading ? 'login-form__submit--disabled' : ''
          }`}
          disabled={!isFormValid || isSubmitting || loading}
        >
          {isSubmitting ? (
            <>
              <span className="login-form__spinner"></span>
              Signing In...
            </>
          ) : (
            'Sign In'
          )}
        </button>

        {showClearSession && (
          <button
            type="button"
            className="login-form__clear-session"
            onClick={handleClearSession}
            disabled={isSubmitting || loading}
          >
            Clear Session & Try Again
          </button>
        )}

        <div className="login-form__footer">
          <p>
            Don't have an account?{' '}
            <button
              type="button"
              className="login-form__link"
              onClick={onSwitchToRegister}
              disabled={isSubmitting || loading}
            >
              Sign up here
            </button>
          </p>
        </div>
      </form>
    </div>
  );
};

export default LoginForm;